import { Events } from '../events';
import { qrSvg } from '../qr-code';
import type { ShareResult } from '../share';

// Self-contained share dialog (plain DOM so we fully control the QR + copy/embed
// affordances). Registered via `show.shareDialog`. Publishes the current scene to
// Firebase Storage and presents the branded viewer link, a QR code, and an iframe
// embed snippet. If publishing isn't configured, falls back to generating links
// from an already-hosted scene URL.
// format a byte count for display
const humanSize = (bytes?: number): string => {
    if (typeof bytes !== 'number') {
        return '';
    }
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const registerShareDialog = (events: Events) => {
    let root: HTMLElement = null;

    const close = () => {
        if (root) {
            root.remove();
            root = null;
        }
    };

    // build a copyable field: a readonly input + a Copy button
    const field = (labelText: string, value: string, multiline = false): HTMLElement => {
        const wrap = document.createElement('div');
        wrap.className = 'share-field';

        const label = document.createElement('div');
        label.className = 'share-field-label';
        label.textContent = labelText;
        wrap.appendChild(label);

        const rowEl = document.createElement('div');
        rowEl.className = 'share-field-row';

        const input: HTMLInputElement | HTMLTextAreaElement = multiline ?
            document.createElement('textarea') : document.createElement('input');
        input.className = 'share-input';
        input.readOnly = true;
        input.value = value;
        if (multiline) {
            (input as HTMLTextAreaElement).rows = 4;
        }
        rowEl.appendChild(input);

        const copyBtn = document.createElement('button');
        copyBtn.className = 'share-btn share-btn-copy';
        copyBtn.textContent = 'Copy';
        copyBtn.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(value);
            } catch (e) {
                input.select();
                document.execCommand('copy');
            }
            copyBtn.textContent = 'Copied';
            setTimeout(() => {
                copyBtn.textContent = 'Copy';
            }, 1400);
        });
        rowEl.appendChild(copyBtn);

        wrap.appendChild(rowEl);
        return wrap;
    };

    // render the success state (links + QR + iframe) into the body
    const renderResult = (body: HTMLElement, result: ShareResult) => {
        body.innerHTML = '';

        // small meta line: what was shared + how big
        const meta = document.createElement('div');
        meta.className = 'share-meta';
        const size = humanSize(result.sizeBytes);
        meta.textContent = size ?
            `Published — your experience is live (${size}).` :
            'Your experience is live. Share any of the options below.';
        body.appendChild(meta);

        // QR code
        const qrWrap = document.createElement('div');
        qrWrap.className = 'share-qr';
        qrWrap.innerHTML = qrSvg(result.viewerUrl, { dark: '#1a1430', light: '#ffffff', border: 2 });

        const qrCol = document.createElement('div');
        qrCol.className = 'share-qr-col';
        qrCol.appendChild(qrWrap);

        const dlQr = document.createElement('button');
        dlQr.className = 'share-btn share-btn-ghost';
        dlQr.textContent = 'Download QR (PNG)';
        dlQr.addEventListener('click', () => downloadQrPng(result.viewerUrl));
        qrCol.appendChild(dlQr);

        const links = document.createElement('div');
        links.className = 'share-links';
        links.appendChild(field('Viewer link', result.viewerUrl));

        const openRow = document.createElement('div');
        openRow.className = 'share-actions';
        const openBtn = document.createElement('button');
        openBtn.className = 'share-btn share-btn-primary';
        openBtn.textContent = 'Open viewer';
        openBtn.addEventListener('click', () => window.open(result.viewerUrl, '_blank')?.focus());
        openRow.appendChild(openBtn);
        links.appendChild(openRow);

        links.appendChild(field('Embed on a website', result.iframe, true));

        const grid = document.createElement('div');
        grid.className = 'share-result-grid';
        grid.appendChild(qrCol);
        grid.appendChild(links);
        body.appendChild(grid);
    };

    // render an unconfigured-fallback state: paste an already-hosted URL
    const renderFallback = (body: HTMLElement) => {
        body.innerHTML = '';

        const note = document.createElement('p');
        note.className = 'share-note';
        note.textContent =
            'One-click publishing isn’t configured yet. Add your Firebase web config in ' +
            'src/firebase-config.ts to upload automatically. In the meantime, host your saved ' +
            '.ssproj anywhere and paste its URL below to generate a viewer link, QR code and embed code.';
        body.appendChild(note);

        const inWrap = document.createElement('div');
        inWrap.className = 'share-field';
        const input = document.createElement('input');
        input.className = 'share-input';
        input.placeholder = 'https://your-host.com/scene.ssproj';
        inWrap.appendChild(input);
        body.appendChild(inWrap);

        const genRow = document.createElement('div');
        genRow.className = 'share-actions';
        const gen = document.createElement('button');
        gen.className = 'share-btn share-btn-primary';
        gen.textContent = 'Generate links';
        gen.addEventListener('click', () => {
            const url = input.value.trim();
            if (!url) {
                return;
            }
            const result = events.invoke('share.fromUrl', url) as ShareResult;
            renderResult(body, result);
        });
        genRow.appendChild(gen);
        body.appendChild(genRow);
    };

    const downloadQrPng = (text: string) => {
        const svg = qrSvg(text, { dark: '#000000', light: '#ffffff', border: 4 });
        const img = new Image();
        const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
        const urlObj = URL.createObjectURL(svgBlob);
        img.onload = () => {
            const size = 1024;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = false;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, size, size);
            ctx.drawImage(img, 0, 0, size, size);
            URL.revokeObjectURL(urlObj);
            canvas.toBlob((blob) => {
                if (!blob) {
                    return;
                }
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = 'simam-experience-qr.png';
                a.click();
                setTimeout(() => URL.revokeObjectURL(a.href), 1000);
            }, 'image/png');
        };
        img.src = urlObj;
    };

    events.function('show.shareDialog', async () => {
        close();

        root = document.createElement('div');
        root.id = 'share-dialog';
        root.addEventListener('pointerdown', (e) => {
            if (e.target === root) {
                close();
            }
        });

        const panel = document.createElement('div');
        panel.className = 'share-panel';

        const header = document.createElement('div');
        header.className = 'share-header';
        const title = document.createElement('div');
        title.className = 'share-title';
        title.textContent = 'Share experience';
        header.appendChild(title);
        const closeBtn = document.createElement('div');
        closeBtn.className = 'share-close';
        closeBtn.textContent = '×';
        closeBtn.addEventListener('click', close);
        header.appendChild(closeBtn);
        panel.appendChild(header);

        const body = document.createElement('div');
        body.className = 'share-body';
        panel.appendChild(body);

        root.appendChild(panel);
        document.body.appendChild(root);

        if (!events.invoke('share.configured')) {
            renderFallback(body);
            return;
        }

        // configured: offer a one-click publish
        const docName = (events.invoke('doc.name') as string) || 'Untitled experience';

        const intro = document.createElement('p');
        intro.className = 'share-note';
        intro.appendChild(document.createTextNode('Publish '));
        const nameEl = document.createElement('strong');
        nameEl.textContent = docName;
        intro.appendChild(nameEl);
        intro.appendChild(document.createTextNode(
            ' to get a shareable viewer link, a QR code and an embed snippet. ' +
            'Clients open the link to explore your experience — no editing tools, just the presentation.'
        ));
        body.appendChild(intro);

        const actions = document.createElement('div');
        actions.className = 'share-actions';
        const publishBtn = document.createElement('button');
        publishBtn.className = 'share-btn share-btn-primary';
        publishBtn.textContent = 'Publish & generate link';
        actions.appendChild(publishBtn);
        body.appendChild(actions);

        publishBtn.addEventListener('click', async () => {
            publishBtn.disabled = true;
            publishBtn.textContent = 'Publishing…';
            try {
                const name = events.invoke('doc.name');
                const result = await events.invoke('share.publish', { name }) as ShareResult;
                renderResult(body, result);
            } catch (err) {
                const msg = document.createElement('p');
                msg.className = 'share-error';
                msg.textContent = `Publish failed: ${(err as Error)?.message ?? err}`;
                body.appendChild(msg);
                publishBtn.disabled = false;
                publishBtn.textContent = 'Try again';
            }
        });
    });
};

export { registerShareDialog };
